import { EvoChain, EvoChainProps } from "@/design-system/Evolution/EvoChain";
import { evolutionsSelector } from "@/redux/evolutions/evolutionsSlice";
import { evolutionChainSteps } from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import type { Evolution } from "@repo/shared/types";
import { useMemo } from "react";
import { useSelector } from "react-redux";

export type EvoChainFromIdsProps = Omit<EvoChainProps, "steps"> & {
  evolutionIds: number[];
  /** Option per level per step; a pathway step shows its own as "[1, 3, 1]". */
  pathwayPath?: number[][];
};

/**
 * Resolves a chain of evolution ids against the catalogue and draws it.
 * Steps whose evolution has not loaded (or no longer exists) read as
 * "Step N" so the chain keeps its shape.
 */
export const EvoChainFromIds = ({
  evolutionIds,
  pathwayPath,
  ...chainProps
}: EvoChainFromIdsProps) => {
  const { t } = useTranslation("evolutions");
  const resolve = useEvolutionsById();

  const steps = useMemo(
    () =>
      evolutionChainSteps({
        evolutionIds,
        pathwayPath,
        resolve,
        fallbackLabel: (index) => t("chain.step", { index: index + 1 }),
      }),
    [evolutionIds, pathwayPath, resolve, t],
  );

  return <EvoChain steps={steps} {...chainProps} />;
};

/**
 * The catalogue as one lookup. Expired evolutions live in their own list and
 * come back flagged, the same way `getEvolutionByIdSelector` reports them.
 */
export const useEvolutionsById = () => {
  const { evolutions, expiredEvolutions } = useSelector(evolutionsSelector);
  return useMemo(() => {
    const byId = new Map<number, Evolution>();
    for (const evolution of expiredEvolutions) {
      byId.set(evolution.id, { ...evolution, expired: true });
    }
    for (const evolution of evolutions) byId.set(evolution.id, evolution);
    return (id: number) => byId.get(id);
  }, [evolutions, expiredEvolutions]);
};
